import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Plus } from "lucide-react";
import { api, downloadFile, errText, fmtUSD, fmtUSD2, TYPE_LABEL } from "@/lib/apiClient";
import { useDicts } from "@/hooks/useDicts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PeriodPicker, { presetRange } from "@/components/PeriodPicker";
import OperationDialog from "@/components/OperationDialog";
import OperationsTable from "@/components/OperationsTable";

const ALL = "all";

export default function PnLCompany() {
  const { companyId } = useParams();
  const [range, setRange] = useState({ ...presetRange("month"), preset: "month" });
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ type: ALL, status: ALL, project_id: ALL, category_id: ALL, search: "" });
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const { dicts, reload } = useDicts();

  const params = useCallback(() => {
    const p = { date_from: range.from, date_to: range.to, company_id: companyId };
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== ALL) p[k] = v;
    });
    return p;
  }, [range.from, range.to, companyId, filters]);

  const load = useCallback(async () => {
    const [pnl, ops] = await Promise.all([
      api.get(`/pnl/${companyId}`, { params: { date_from: range.from, date_to: range.to } }),
      api.get("/operations", { params: params() }),
    ]);
    setData({ ...pnl.data, operations: ops.data });
  }, [companyId, range.from, range.to, params]);

  useEffect(() => {
    load();
  }, [load]);

  const confirmOp = async (op) => {
    await api.post(`/operations/${op.id}/confirm`);
    toast.success("Операция подтверждена");
    load();
  };

  const deleteOp = async (op) => {
    try {
      await api.delete(`/operations/${op.id}`);
      toast.success("Операция удалена");
      load();
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    }
  };

  const t = data?.totals || {};

  return (
    <div className="space-y-6 md:space-y-8">
      <Link to="/pnl" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-pnl">
        <ArrowLeft className="h-4 w-4" /> Все компании
      </Link>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl" data-testid="company-title">{data?.company?.name || "…"}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Остаток на сегодня:{" "}
            <span className="font-mono font-semibold text-foreground" data-testid="company-balance">{fmtUSD2(data?.balance)}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" data-testid="company-export-excel"
            onClick={() => downloadFile("/operations/export", { ...params(), format: "excel" }, "mayo_company.xlsx")}>
            <Download className="h-4 w-4" /> Excel
          </Button>
          <Button variant="outline" className="gap-2" data-testid="company-export-pdf"
            onClick={() => downloadFile("/operations/export", { ...params(), format: "pdf" }, "mayo_company.pdf")}>
            <Download className="h-4 w-4" /> PDF
          </Button>
          <Button className="gap-2" data-testid="company-new-operation" onClick={() => { setEditing(null); setDialog(true); }}>
            <Plus className="h-4 w-4" /> Операция
          </Button>
        </div>
      </div>

      <PeriodPicker range={range} onChange={setRange} />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[
          ["Заработано", fmtUSD2(t.income), `Ожидается ${fmtUSD(t.income_planned)}`, "text-income", "earned"],
          ["Потрачено", fmtUSD2(t.expense), `Запланировано ${fmtUSD(t.expense_planned)}`, "text-expense", "spent"],
          ["Переводы (в т.ч. в Доход/Расход)", `+${fmtUSD(t.transfer_in)} / −${fmtUSD(t.transfer_out)}`, `Ожидается ${fmtUSD(t.transfer_planned)}`, "text-transfer", "transfers"],
          ["Чистый результат", fmtUSD2(t.net), `${t.count || 0} операций`, "", "net"],
        ].map(([label, value, sub, cls, tid]) => (
          <Card className="glass metric-card" key={tid} data-testid={`company-metric-${tid}`}>
            <CardContent className="p-5">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
              <div className={`mt-2 font-mono text-xl font-bold lg:text-2xl ${cls}`}>{value}</div>
              <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="glass">
        <CardContent className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-5">
          <Input placeholder="Поиск по названию" value={filters.search} data-testid="filter-search"
            onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
          <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
            <SelectTrigger data-testid="filter-type"><SelectValue placeholder="Тип" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Все типы</SelectItem>
              {Object.entries(TYPE_LABEL).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Статус" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Все статусы</SelectItem>
              <SelectItem value="confirmed">Подтверждено</SelectItem>
              <SelectItem value="planned">Запланировано</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.project_id} onValueChange={(v) => setFilters({ ...filters, project_id: v })}>
            <SelectTrigger data-testid="filter-project"><SelectValue placeholder="Проект" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Все проекты</SelectItem>
              {dicts.projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.category_id} onValueChange={(v) => setFilters({ ...filters, category_id: v })}>
            <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Категория" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Все категории</SelectItem>
              {dicts.categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <OperationsTable
        operations={data?.operations || []}
        onConfirm={confirmOp}
        onEdit={(o) => { setEditing(o); setDialog(true); }}
        onDelete={deleteOp}
      />

      <OperationDialog
        open={dialog}
        onOpenChange={setDialog}
        dicts={dicts}
        reloadDicts={reload}
        operation={editing}
        defaultCompany={companyId}
        onSaved={load}
      />
    </div>
  );
}
